import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, ShieldCheck, MapPin, FileText } from "lucide-react";

/**
 * Wiederverwendbarer Promo-Block für den "Wer darf was im Pflegedienst"
 * Delegations-Atlas (253 Seiten · alle 16 Bundesländer · 42 Tätigkeiten).
 *
 * Drei Varianten:
 *   - variant="b2b"        → Sand/Terra · Fokus PDL/QM/MD-Sicherheit
 *   - variant="b2c"        → Cream/DarkRed · Fokus Pflegekraft Identität/Sicherheit
 *   - variant="ads"        → kompakter Cross-Sell-Block für Google-Ads-LPs
 */
export default function AtlasPromo({ variant = "b2b", testId = "atlas-promo" }) {
  const isAds = variant === "ads";
  const isB2C = variant === "b2c";

  const palette = isAds
    ? { wrap: "bg-cream-100 border border-cream-300", kicker: "text-brand-darkred", title: "text-sand-800", desc: "text-sand-600", btn: "bg-terra hover:bg-terra-dark text-white" }
    : isB2C
      ? { wrap: "bg-brand-rose/25 border border-brand-rose/50", kicker: "text-brand-darkred", title: "text-sand-800", desc: "text-sand-700", btn: "bg-terra hover:bg-terra-dark text-white" }
      : { wrap: "bg-peach-50 border border-cream-300", kicker: "text-terra", title: "text-sand-800", desc: "text-sand-700", btn: "bg-terra hover:bg-terra-dark text-white" };

  const copy = isB2C
    ? {
        kicker: "Atlas · 251 Seiten · alle 16 Bundesländer · Vorschau gratis",
        title: "„Darf ich das überhaupt?“",
        accent: "Der Atlas, der Schluss macht mit Unsicherheit.",
        desc: "42 delegierbare Tätigkeiten, für jede die Antwort in deinem Bundesland. Aus reiner Pflege-Perspektive geschrieben. 17-Seiten-Vorschau sofort als PDF.",
      }
    : isAds
      ? {
          kicker: "Bonus zur Weiterbildung · gratis PDF",
          title: "Was darfst du nach LG1/LG2 in deinem Bundesland?",
          accent: "Die 16-BL-Matrix als 200+-Seiten-Atlas.",
          desc: "Wir haben jede der 42 Behandlungspflege-Tätigkeiten gegen alle 16 Landes-Rahmenverträge geprüft. Inkl. Pre-Flight-Checkliste, Mustertexten, Haftungsfallen. Kostenlos.",
        }
      : {
          kicker: "Atlas · 251 Seiten · alle 16 Bundesländer · Vorschau gratis",
          title: "„Wer hat das freigegeben?“",
          accent: "Die Antwort des MD-Prüfers steht jetzt im QM-Ordner.",
          desc: "Der Delegations-Atlas mit allen 42 Tätigkeiten · 16-Bundesländer-Matrix · 4-Label-Pre-Flight (Gesetz · Landesvertrag · MD-QM · Praxistipp) · 7 Haftungsfallen pro Tätigkeit · Mustertexten. Genau das, was QM/PDL ablegen will.",
        };

  const stats = [
    { icon: BookOpen, label: "Tätigkeiten", value: "42" },
    { icon: MapPin, label: "Bundesländer", value: "alle 16" },
    { icon: ShieldCheck, label: "Label-System", value: "4-fach" },
    { icon: FileText, label: "Vollversion", value: "200+ Seiten" },
  ];

  return (
    <section data-testid={testId} className={`rounded-3xl overflow-hidden relative ${palette.wrap}`}>
      <div className="relative p-7 sm:p-10 lg:p-12 lg:grid lg:grid-cols-[1fr_auto] lg:gap-10 lg:items-center">
        <div>
        <div className={`text-[10.5px] uppercase tracking-[0.28em] font-semibold ${palette.kicker}`}>
          {copy.kicker}
        </div>
        <h3 className={`mt-4 font-display leading-[1.1] text-2xl sm:text-3xl lg:text-[2.2rem] ${palette.title}`}>
          {copy.title} <span className={`italic block sm:inline ${palette.kicker}`}>{copy.accent}</span>
        </h3>
        <p className={`mt-5 leading-[1.65] text-[14.5px] sm:text-[15.5px] max-w-2xl ${palette.desc}`}>
          {copy.desc}
        </p>

        <ul className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map(({ icon: Icon, label, value }) => (
            <li key={label} className={`rounded-2xl px-4 py-3 ${isAds ? "bg-white border border-cream-300" : "bg-white/8 border border-white/12"}`}>
              <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold ${palette.kicker}`}>
                <Icon className="h-3 w-3" strokeWidth={2} /> {label}
              </div>
              <div className={`mt-1 font-display text-lg sm:text-xl ${palette.title}`}>{value}</div>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Link to="/leitfaden/wer-darf-was-atlas" data-testid={`${testId}-cta`}
            className={`inline-flex items-center gap-2 rounded-full px-6 sm:px-7 h-12 text-[13.5px] sm:text-sm font-semibold transition-colors ${palette.btn}`}>
            Vorschau gratis anfordern <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
          <Link to="/wer-darf-was" data-testid={`${testId}-vorschau`}
            className={`inline-flex items-center text-[12.5px] underline underline-offset-4 hover:no-underline transition text-sand-600 hover:text-sand-800`}>
            Was steckt drin? Vorschau & FAQ →
          </Link>
        </div>
        </div>

        {/* Editorial · Atlas wird gelesen */}
        <div className="hidden lg:block shrink-0">
          <div className="relative">
            <img
              src="/images/brand/atlas-lesen-fenster.jpg"
              alt="Eine Pflegekraft liest den Wer-darf-was-Atlas am Fenster"
              className="w-56 xl:w-64 aspect-[4/5] object-cover shadow-[0_22px_45px_-18px_rgba(58,52,46,0.45)]"
              loading="lazy"
            />
            <div className="absolute -bottom-3 -right-3 h-full w-full border border-saffron-dark/50 pointer-events-none" aria-hidden="true" />
          </div>
          <div className="mt-4 text-center text-[9.5px] uppercase tracking-[0.26em] font-bold text-terra">
            200+ Seiten · Gratis
          </div>
        </div>
      </div>
    </section>
  );
}
